'use es6';

import PropTypes from 'prop-types';
import { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import Immutable from 'immutable';
import FormattedMessage from 'I18n/components/FormattedMessage';
import H2 from 'UIComponents/elements/headings/H2';
import UIButton from 'UIComponents/button/UIButton';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIPanel from 'UIComponents/panel/UIPanel';
import UIPanelBody from 'UIComponents/panel/UIPanelBody';
import UIPanelFooter from 'UIComponents/panel/UIPanelFooter';
import UIPanelHeader from 'UIComponents/panel/UIPanelHeader';
import UIPanelSection from 'UIComponents/panel/UIPanelSection';
import UIForm from 'UIComponents/form/UIForm';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UIToggleGroup from 'UIComponents/input/UIToggleGroup';
import { RequestStatus, ObjectCategory } from 'FileManagerCore/Constants';
import FileVisibility from 'FileManagerCore/components/FileVisibility';
import FileVisibilityDisplayOptions from 'FileManagerCore/enums/FileVisibilityDisplayOptions';
import { bulkChangeVisibility, getNestedFolderFileCount } from '../../actions/Bulk';
import { getTotalFileCount, getSelectedFilesAndFolders, getTotalNestedFileCountRequestStatus, getBulkChangePublicAccessibilityRequestStatus } from '../../selectors/Selection';
import UsageTracker from '../../utils/usageTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function getI18nKey(suffix) {
  return `FileManagerDashboard.bulkChangeFileVisibility.${suffix}`;
}
class BulkFileVisibilityPanel extends Component {
  constructor(...args) {
    super(...args);
    this.state = {
      isConfirmationModalOpen: false
    };
    this.fetchNestedCount = () => {
      this.props.fetchNestedCount(this.props.bulkSelection.filter(fileOrFolder => fileOrFolder.get('category') === ObjectCategory.FOLDER).keySeq().toList());
    };
    this.handleConfirmBulkChangeVisibility = selectedFileVisibilitySetting => {
      const {
        onBulkChangeVisibility,
        bulkSelection,
        totalFileCount
      } = this.props;
      this.trackSaveBulkFileVisibilityChangeInteraction();
      onBulkChangeVisibility(selectedFileVisibilitySetting, bulkSelection, totalFileCount, this.handleCloseOpenPanelAndModal);
    };
    this.handleSave = () => {
      this.setState({
        isConfirmationModalOpen: true
      });
    };
    this.handleCloseConfirmationModal = () => {
      this.setState({
        isConfirmationModalOpen: false
      });
    };
    this.handleCloseOpenPanelAndModal = () => {
      this.handleCloseConfirmationModal();
      this.props.onClose();
    };
    this.onVisibilitySettingSelected = selectedFileVisibilitySetting => {
      this.setState({
        selectedFileVisibilitySetting
      });
    };
  }
  componentDidMount() {
    this.fetchNestedCount();
  }
  componentDidUpdate(previousProps) {
    if (!this.props.bulkSelection.equals(previousProps.bulkSelection)) {
      this.fetchNestedCount();
    }
    if (previousProps.bulkChangeVisibilityRequestStatus === RequestStatus.PENDING && this.props.bulkChangeVisibilityRequestStatus === RequestStatus.SUCCEEDED) {
      this.props.onClose();
    }
  }
  trackSaveBulkFileVisibilityChangeInteraction() {
    const {
      totalFileCount
    } = this.props;
    const {
      selectedFileVisibilitySetting
    } = this.state;
    UsageTracker.track('fileManagerFileVisibility', {
      action: 'submit visibility change',
      isBulk: true,
      fileCount: totalFileCount,
      visibility: selectedFileVisibilitySetting
    });
  }
  render() {
    const {
      selectedFileVisibilitySetting,
      isConfirmationModalOpen
    } = this.state;
    const {
      totalNestedFileCountRequestStatus,
      bulkChangeVisibilityRequestStatus,
      totalFileCount
    } = this.props;
    const isRequestPending = bulkChangeVisibilityRequestStatus === RequestStatus.PENDING;
    return /*#__PURE__*/_jsx(Fragment, {
      children: /*#__PURE__*/_jsxs(UIPanel, {
        width: 540,
        children: [/*#__PURE__*/_jsx(UIDialogCloseButton, {
          onClick: this.props.onClose,
          disabled: isRequestPending
        }), /*#__PURE__*/_jsx(UIPanelHeader, {
          children: /*#__PURE__*/_jsx(H2, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: getI18nKey('panelTitle'),
              options: {
                fileCount: totalFileCount
              }
            })
          })
        }), /*#__PURE__*/_jsx(UIPanelBody, {
          children: /*#__PURE__*/_jsxs(UIPanelSection, {
            children: [/*#__PURE__*/_jsx("div", {
              className: "m-y-5",
              children: typeof totalFileCount === 'number' && /*#__PURE__*/_jsx(FormattedMessage, {
                message: getI18nKey('panelBodyWithNestedFileCount'),
                options: {
                  count: totalFileCount
                }
              })
            }), /*#__PURE__*/_jsx(UIForm, {
              children: /*#__PURE__*/_jsx(UIFormControl, {
                children: /*#__PURE__*/_jsx(UIToggleGroup, {
                  className: "m-y-2",
                  name: "bulk-file-visibility-input-group",
                  children: /*#__PURE__*/_jsx(FileVisibility, {
                    use: FileVisibilityDisplayOptions.RADIO,
                    fileCount: totalFileCount,
                    onConfirm: this.handleConfirmBulkChangeVisibility,
                    onCancel: this.handleCloseConfirmationModal,
                    changeVisibilityRequestStatus: bulkChangeVisibilityRequestStatus,
                    fileCountRequestStatus: totalNestedFileCountRequestStatus,
                    onVisibilitySelected: this.onVisibilitySettingSelected,
                    showModal: isConfirmationModalOpen,
                    onRetryGetFileCount: this.fetchNestedCount
                  })
                })
              })
            })]
          })
        }), /*#__PURE__*/_jsxs(UIPanelFooter, {
          children: [/*#__PURE__*/_jsx(UILoadingButton, {
            disabled: !selectedFileVisibilitySetting || isRequestPending,
            loading: isRequestPending,
            onClick: this.handleSave,
            use: "primary",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: getI18nKey('save')
            })
          }), /*#__PURE__*/_jsx(UIButton, {
            onClick: this.props.onClose,
            use: "secondary",
            disabled: isRequestPending,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: getI18nKey('cancel')
            })
          })]
        })]
      })
    });
  }
}
BulkFileVisibilityPanel.propTypes = {
  totalFileCount: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  fetchNestedCount: PropTypes.func.isRequired,
  onBulkChangeVisibility: PropTypes.func.isRequired,
  bulkSelection: PropTypes.instanceOf(Immutable.Map).isRequired,
  bulkChangeVisibilityRequestStatus: PropTypes.oneOf(Object.keys(RequestStatus)).isRequired,
  totalNestedFileCountRequestStatus: PropTypes.oneOf(Object.keys(RequestStatus)).isRequired
};
const mapStateToProps = state => {
  return {
    totalFileCount: getTotalFileCount(state),
    bulkSelection: getSelectedFilesAndFolders(state),
    bulkChangeVisibilityRequestStatus: getBulkChangePublicAccessibilityRequestStatus(state),
    totalNestedFileCountRequestStatus: getTotalNestedFileCountRequestStatus(state)
  };
};
const mapDispatchToProps = {
  onBulkChangeVisibility: bulkChangeVisibility,
  fetchNestedCount: getNestedFolderFileCount
};
export default connect(mapStateToProps, mapDispatchToProps)(BulkFileVisibilityPanel);